import { AnalyticsStore, ContextStore, ExperimentStore, PageStore } from 'bernie-plugin-mobx';
import { inject, observer } from 'mobx-react';
import { reaction } from 'mobx';
import { useHistory, useLocation } from 'react-router-dom';
import * as React from 'react';

import { ClientLogger } from 'bernie-client';
import { ErrorBoundary } from 'bernie-view';

import { LodgingQueryParamsProvider, SearchFormInputs } from '@shared-ui/search-tools-lodging';
import { useExperiment } from '@shared-ui/experiment-context';
import { useLocalization } from '@shared-ui/localization-context';

import { SearchResultsStore } from 'src/common/stores/search-results-store';
import { UIPageStore } from 'stores/ui-page-store';

import {
  isAlternateDestinationFeatureGate,
  isMetaChannelDefaultDatesSearch,
  isSEMChannelDefaultDatesSearch,
  isShowWeekdayExperiment,
  isSRPCalendarAutoOpenExperiment,
  isStickySearchFormExperiment,
  isVrbexExperiment,
  updateResultsWithNewCriteria,
} from './universal-search-form-utils';
import { onClickScrollToTarget } from 'utils/smooth-scroll';

import { EssClientLogger } from 'typeahead-client-logger';
import { SearchFormWrapping } from './search-form-wrapping';

export interface UniversalSearchFormWrapperProps {
  analytics?: AnalyticsStore;
  searchResults?: SearchResultsStore;
  experiment?: ExperimentStore;
  uiPage?: UIPageStore;
  page?: PageStore;
  context?: ContextStore;
}

const UniversalSearchFormWrapper: React.FC<UniversalSearchFormWrapperProps> = inject(
  'analytics',
  'searchResults',
  'experiment',
  'uiPage',
  'page',
  'context'
)(
  observer(({ analytics, searchResults, experiment, uiPage, context, page }: UniversalSearchFormWrapperProps) => {
    const location = useLocation();
    const history = useHistory();
    const l10n = useLocalization();

    const [datePickerOpen, setDatePickerOpen] = React.useState(false);

    const searchQueryParams = new URLSearchParams(location.search);
    const queryParamStartDate = searchQueryParams.get('startDate') || '';
    const queryParamEndDate = searchQueryParams.get('endDate') || '';
    const queryParamLatLong = searchQueryParams.get('latLong') || '';
    const isMobile = context?.deviceInformation?.mobile && !context?.deviceInformation?.tablet;
    const isMetaOrSEMChannelDefaultDatesSearch = Boolean(
      isMetaChannelDefaultDatesSearch(searchQueryParams) || isSEMChannelDefaultDatesSearch(searchQueryParams)
    );
    const isSRPCalendarAutoOpenExperimentEnabled = Boolean(experiment && context && isSRPCalendarAutoOpenExperiment(experiment, context));

    const { exposure: autoOpenPlayBackExposure, logExperiment: autoOpenPlayBackLogExperiment } = useExperiment(
      'SRP_mWeb_Auto_open_playback_component'
    );

    const isSRPAutoOpenPlaybackExperimentEnabled = Boolean(
      isMobile && (autoOpenPlayBackExposure.bucket === 1 || autoOpenPlayBackExposure.bucket === 2)
    );
    const isDateFieldInitialFocus = Boolean(isMetaOrSEMChannelDefaultDatesSearch && isMobile && autoOpenPlayBackExposure.bucket === 2);

    const isStickySearchFormExperimentEnabled = Boolean(
      experiment && context && page && isStickySearchFormExperiment(experiment, context, page)
    );
    const isShowWeekdayExperimentEnabled = Boolean(experiment && context && isShowWeekdayExperiment(experiment, context));
    const isAlternateDestinationEnabled = Boolean(experiment && context && isAlternateDestinationFeatureGate(experiment, context));
    const searchFormRef = React.useRef<HTMLDivElement>(null);

    // Auto open calendar when user lands on SRP from META/SEM channel and had not entered dates
    React.useEffect(() => {
      if (isMetaOrSEMChannelDefaultDatesSearch && isSRPCalendarAutoOpenExperimentEnabled && !isMobile) {
        setDatePickerOpen(true);
      }
    }, []);

    React.useEffect(() => {
      // exposure should be logged only for mobile devices.
      if (isMetaOrSEMChannelDefaultDatesSearch && isMobile) {
        autoOpenPlayBackLogExperiment(autoOpenPlayBackExposure);
      }
    }, [isMetaOrSEMChannelDefaultDatesSearch, autoOpenPlayBackLogExperiment, autoOpenPlayBackExposure, isMobile]);

    // Auto open calender for dateless SRP for vrbex
    React.useEffect(() => {
      if (experiment && isVrbexExperiment(experiment) && !isMobile) {
        if (!queryParamStartDate && !queryParamEndDate) {
          setDatePickerOpen(true);
        }
      }
    }, []);

    React.useEffect(() => {
      const uiPageOpenDatePickerDisposer = reaction(
        () => uiPage?.openNewDatePickerEvent,
        openNewDatePickerEvent => {
          if (openNewDatePickerEvent) {
            setDatePickerOpen(true);
            searchFormRef.current?.scrollIntoView({
              block: 'start',
              behavior: 'smooth',
            });
          }
        }
      );

      return () => uiPageOpenDatePickerDisposer();
    }, [uiPage?.openNewDatePickerEvent]);

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>, inputs?: SearchFormInputs) => {
      event.preventDefault();
      analytics?.resetAnalyticsForNewPage();

      if (searchResults && experiment && inputs) {
        updateResultsWithNewCriteria(inputs, location, history, searchResults, experiment, l10n);
      }
      if (isStickySearchFormExperimentEnabled) {
        onClickScrollToTarget({ scrollTo: 0 });
      }
    };

    const essLogger = new EssClientLogger(analytics, ClientLogger, searchResults?.searchCriteria.destination, experiment?.exposures);

    return (
      <ErrorBoundary label="hotel.search.universal-search-form">
        <LodgingQueryParamsProvider urlSearchParams={new URLSearchParams(useLocation().search)}>
          <SearchFormWrapping
            dateLocalFormat={context?.localDateFormat}
            queryParamEndDate={queryParamEndDate}
            queryParamStartDate={queryParamStartDate}
            handleSubmit={handleSubmit}
            essLogger={essLogger}
            showDatePicker={datePickerOpen}
            toggleDatePicker={setDatePickerOpen}
            autoOpenPlayback={isMetaOrSEMChannelDefaultDatesSearch && isSRPAutoOpenPlaybackExperimentEnabled}
            dateFieldInitialFocus={isDateFieldInitialFocus}
            stickySearchForm={isStickySearchFormExperimentEnabled}
            showWeekday={isShowWeekdayExperimentEnabled}
            queryParamLatLong={queryParamLatLong}
            alternateDestination={isAlternateDestinationEnabled}
            ref={searchFormRef}
          />
        </LodgingQueryParamsProvider>
      </ErrorBoundary>
    );
  })
);

export const SharedUiUniversalSearchFormWrapper = UniversalSearchFormWrapper;
SharedUiUniversalSearchFormWrapper.displayName = 'SharedUiUniversalSearchFormWrapper';
